import { PAGE_URLS } from "@constants/common";
import { useFetchProfileQuery } from "@features/common/commonApi";
import PageLoader from "@features/common/pageLoader/PageLoader";
import { useAppDispatch } from "@redux/hooks";
import { EnabledNavigation, setProfileData } from "@redux/slices/CommonSlice";
import { FC, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

/**
 * @component AccessControlGuard
 * @description Route-level permission guard that validates user access to protected routes.
 * Prevents users from manually navigating to routes they don't have permission to access.
 *
 * @flow
 * 1. Profile data is fetched at router level (already cached)
 * 2. Guard extracts enabled_navigation permissions from Redux
 * 3. Compares current route against accessMap
 * 4. Allows access if permission is true, redirects to /not-found if false
 *
 * @usage
 * Place this guard BEFORE CommonLayout in router config:
 * <Route element={<AccessControlGuard />}>
 *   <Route element={<CommonLayout />}>
 *     <Route path="/issue-credits" element={<IssueCredits />} />
 *   </Route>
 * </Route>
 */
const AccessControlGuard: FC = () => {
  // ============================================================================
  // Hooks
  // ============================================================================

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // ============================================================================
  // Profile Data Fetching
  // ============================================================================
  /**
   * Fetch profile data (cached at router level, returns instantly)
   * This is redundant call but ensures guard has profile even if router fails
   * In practice, profile is already loaded and cached by RTK Query
   */
  const { data: profileResponse, isLoading: isProfileLoading } =
    useFetchProfileQuery();

  // 2. Sync to Redux whenever data changes
  useEffect(() => {
    if (profileResponse?.data) {
      const {
        corporate_name,
        email,
        full_name,
        first_name,
        last_name,
        enabled_navigation,
      } = profileResponse.data;

      dispatch(
        setProfileData({
          corporate_name: corporate_name || "",
          email: email || "",
          full_name: full_name || "",
          first_name: first_name || "",
          last_name: last_name || "",
          enabled_navigation: enabled_navigation as EnabledNavigation,
        }),
      );
    }
  }, [profileResponse, dispatch]);

  // 3. Memoize the permissions to prevent unnecessary calculations
  const permissions = useMemo(() => {
    const nav = profileResponse?.data?.enabled_navigation;
    return {
      hasReport: nav?.reports,
      hasCreditReport: nav?.credit_reports,
      hasIssueCredit: nav?.credit_upload,
      hasExpiryReminder: nav?.expiry_reminders,
      hasDrafts: nav?.saved_orders,
      hasUserManagement: nav?.user_management
    };
  }, [profileResponse]);

  // 4. Access Guard Logic
  useEffect(() => {
    // Only run guard logic once profile is loaded
    if (isProfileLoading || !profileResponse) return;

    const normalizedPath = pathname.replace(/\/+$/, "");

    // Logic Map: Path -> Permission check
    const accessMap: Record<string, boolean | undefined> = {
      [PAGE_URLS.REPORTS]: permissions.hasReport,
      [PAGE_URLS.SEND_REMINDER]: permissions.hasExpiryReminder,
      [PAGE_URLS.USER_MANAGEMENT]: permissions.hasUserManagement,
      [PAGE_URLS.DRAFTS]: permissions.hasDrafts,
      [PAGE_URLS.ISSUE_CREDITS]: permissions.hasIssueCredit,
      [PAGE_URLS.CREDIT_REPORTS]: permissions.hasCreditReport,
      [PAGE_URLS.CREDITS_LISTING]: permissions.hasIssueCredit,
      [PAGE_URLS.ISSUE_CREDITS_SUCCESS]: permissions.hasIssueCredit,
    };

    // Check if current path exists in the map and if user has access
    if (normalizedPath in accessMap && !accessMap[normalizedPath]) {
      navigate(PAGE_URLS.ACCESS_DENIED, { replace: true });
    }
  }, [pathname, permissions, isProfileLoading, profileResponse, navigate]);

  if (isProfileLoading) return <PageLoader />;

  return <Outlet />;
};

export default AccessControlGuard;
